import React from "react";
import { twMerge } from "tailwind-merge";

export interface CardProps {
  children: React.ReactNode;
  className?: string;
  elevation?: 0 | 1 | 2 | 3;
  bordered?: boolean;
  hoverEffect?: boolean;
  onClick?: () => void;
  variant?: "default" | "terracotta" | "cream" | "beige" | "brown";
}

const elevationClasses = {
  0: "shadow-none",
  1: "shadow-sm",
  2: "shadow-md",
  3: "shadow-lg",
};

const variantClasses = {
  default: "bg-white",
  terracotta: "bg-terracotta-500 border-terracotta-200",
  cream: "bg-cream-50 border-cream-200",
  beige: "bg-beige-50 border-beige-200",
  brown: "bg-brown-50 border-brown-200",
};

const Card: React.FC<CardProps> = ({
  children,
  className,
  elevation = 1,
  bordered = true,
  hoverEffect = false,
  variant = "default",
  onClick,
  ...props
}) => {
  const cardClasses = twMerge(
    // Base styles
    "rounded-lg overflow-hidden",

    // Variant styles
    variantClasses[variant],

    // Elevation
    elevationClasses[elevation],

    // Border
    bordered
      ? variant === "default"
        ? "border border-cream-200"
        : "border"
      : "",

    // Interactive styles
    onClick ? "cursor-pointer" : "",
    hoverEffect ? "transition-all duration-200 hover:shadow-md" : "",
    variant !== "default" && hoverEffect
      ? variant === "terracotta"
        ? "hover:bg-terracotta-500"
        : variant === "cream"
        ? "hover:bg-cream-100"
        : variant === "beige"
        ? "hover:bg-beige-100"
        : "hover:bg-brown-100"
      : "",

    // Custom classes
    className
  );

  return (
    <div className={cardClasses} onClick={onClick} {...props}>
      {children}
    </div>
  );
};

export interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
  divider?: boolean;
  variant?: "default" | "terracotta" | "cream" | "beige" | "brown";
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  children,
  className,
  divider = false,
  variant = "default",
  ...props
}) => {
  const headerClasses = twMerge(
    "px-6 py-4",
    divider
      ? variant === "default"
        ? "border-b border-gray-200"
        : variant === "terracotta"
        ? "border-b border-terracotta-200"
        : variant === "cream"
        ? "border-b border-cream-200"
        : variant === "beige"
        ? "border-b border-beige-200"
        : "border-b border-brown-200"
      : "",
    className
  );

  return (
    <div className={headerClasses} {...props}>
      {children}
    </div>
  );
};

export interface CardBodyProps {
  children: React.ReactNode;
  className?: string;
}

export const CardBody: React.FC<CardBodyProps> = ({
  children,
  className,
  ...props
}) => {
  const bodyClasses = twMerge("px-6 py-4", className);

  return (
    <div className={bodyClasses} {...props}>
      {children}
    </div>
  );
};

export interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
  divider?: boolean;
  variant?: "default" | "terracotta" | "cream" | "beige" | "brown";
}

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className,
  divider = false,
  variant = "default",
  ...props
}) => {
  const footerClasses = twMerge(
    "px-6 py-4",
    divider
      ? variant === "default"
        ? "border-t border-gray-200"
        : variant === "terracotta"
        ? "border-t border-terracotta-200"
        : variant === "cream"
        ? "border-t border-cream-200"
        : variant === "beige"
        ? "border-t border-beige-200"
        : "border-t border-brown-200"
      : "",
    className
  );

  return (
    <div className={footerClasses} {...props}>
      {children}
    </div>
  );
};

export default Object.assign(Card, {
  Header: CardHeader,
  Body: CardBody,
  Footer: CardFooter,
});
